import _ from 'underscore';
import Backbone from 'backbone';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import { GridCustomizedPopupEditor } from '@bingads-webui-campaign/grid-customized-popup-editor';
import { BidAdjustmentView } from '@bingads-webui-campaign/bid-adjustment';
import { isXandrServing } from '@bingads-webui-campaign-react/constants';
import bidAdjColumnTemplate from './template/bid-adjustment-column.pug';
import { TargetingLevel } from '../config';
import { TargetingSettingsContext } from '../targeting-settings-context';
import extendedBidAdjTmpl from './template/extended-bid-adjustment.pug';

class ExtendedBidAdjustmentView extends Backbone.View {
  initialize(options) {
    this.options = options;
  }

  render() {
    const {
      i18n,
      value,
      customValidators,
      currentCampaign,
      criterion,
    } = this.options;

    this.$el.html(extendedBidAdjTmpl({
      i18n: this.options.i18n,
      isCommission: _.chain(currentCampaign).result('BiddingScheme').result('Type').value() === 'Commission',
      isDevice: _.chain(criterion).result('Type').value() === 'Device',
    }));

    if (this.bidAdjustmentView) {
      this.bidAdjustmentView.remove();
    }

    this.bidAdjustmentView = new BidAdjustmentView({
      value,
      i18n,
      customValidators,
    });

    this.$('.bid-adjustment').empty().append(this.bidAdjustmentView.render().$el);
    this.listenTo(this.bidAdjustmentView, 'all', this.trigger);

    return this;
  }

  remove() {
    this.bidAdjustmentView.remove();
    return super.remove();
  }
}

export default class ColumnBidAdjustment extends CampaignuiBaseColumn {
  constructor({
    i18n, level, parentView, customValidators,
  }) {
    const localizedTitle = i18n.getString('TargetingSettings_Grid_Column_BidAdj');
    const { isDeprecatedLocation } = TargetingSettingsContext;
    const permissions = TargetingSettingsContext.pageContext.Permissions;
    const isMSANBoostEnabled = _.result(permissions, 'IsAmplifyEnabled', false);
    const isHideBidAdjustmentForBoostEnabled = isMSANBoostEnabled && _.result(permissions, ['dynamic', 'HideBidAdjustmentForBoost']);

    super({
      name: 'BidAdj',
      title: 'TargetingSettings_Grid_Column_BidAdj',
      property: level === TargetingLevel.AdGroup ?
        'Model.BiddableAdGroupCriterion/CriterionBid' :
        'Model.BiddableCampaignCriterion/CriterionBid',
      sortable: 'CriterionBid',
      type: 'number',
      format: 'percentage',
      helpId: 'pop_BA_Settings_BidAdjustment',
      i18n,
      parentView,
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
      enableEdit: (item) => {
        if (isDeprecatedLocation(item.Criterion)) { // non-editable for location deprecation
          return false;
        }

        const isCommission = _.chain(item.Campaign).result('BiddingScheme').result('Type').value() === 'Commission';
        const isAdGroupLevel = level === TargetingLevel.AdGroup;
        const isHotelCampaign = _.chain(item.Campaign).result('CampaignType').value() === 'Hotel';
        const isPMaxCampaign = _.chain(item.Campaign).result('CampaignType').value() === 'PerformanceMax';
        const isLinkedInCampaign = _.chain(item.Campaign).result('CampaignType').value() === 'LinkedIn';
        const criterionType = _.chain(item.Criterion).result('Type').value();
        const isXandr = isXandrServing(_.chain(item.Campaign).result('CampaignSubType').value(), _.chain(item.Campaign).result('CampaignSettings').value());
        const isLocation = criterionType === 'Location' || criterionType === 'Radius';
        const isAdSchedule = criterionType === 'DayTime';
        const isDevice = criterionType === 'Device';

        if (isCommission && (isLocation || isAdSchedule || (isAdGroupLevel && isDevice))) {
          return false;
        } else if ((isAdGroupLevel && (isHotelCampaign && (isLocation || isAdSchedule)))) {
          return false;
        } else if (isPMaxCampaign && (isLocation || isAdSchedule || isDevice)) {
          return false;
        } else if (isXandr && (isLocation || isAdSchedule || isDevice)) {
          return false;
        } else if (isHideBidAdjustmentForBoostEnabled) {
          return false;
        } else if (isLinkedInCampaign) {
          return false;
        }

        return item.Campaign && item.Campaign.CampaignSubType !== 'ShoppingSmartAds'; // non-editable for smart shopping campaign
      },
    });

    this.i18n = i18n;
    this.customValidators = customValidators;
    this.level = level;
    this.isHideBidAdjustmentForBoostEnabled = isHideBidAdjustmentForBoostEnabled;
  }

  onPrompt({ item, newPosition }) {
    if (item.LevelOfDetails === 'Campaign') {
      this.parentView.trigger('inlineEdit', item);
    } else {
      const bidAdjustmentView = new ExtendedBidAdjustmentView({
        value: item.CriterionBid.Multiplier,
        i18n: this.i18n,
        customValidators: _.isFunction(this.customValidators)
          ? this.customValidators(item.Criterion, item.Campaign)
          : this.customValidators,
        currentCampaign: item.Campaign,
        criterion: item.Criterion,
      }).render();

      const popupEditor = new GridCustomizedPopupEditor({
        editorView: bidAdjustmentView,
        cellPosition: newPosition,
        tableContainer: '.bingadsgrid',
        className: 'grid-popupeditor',
        parentContainerSelector: '.grid-and-disclaim-container',
      }).render();

      popupEditor.on('close', (model) => {
        this.parentView.grid.gridView.trigger('didEdit', model, item, 'BidAdjustments');
      });
    }
  }

  get property() {
    return {
      get: ({
        CriterionBid, LevelOfDetails, Campaign, Criterion, Id,
      }) => {
        if (_.isUndefined(CriterionBid)) {
          return null;
        }

        const inherited = LevelOfDetails === 'Campaign';

        let multiplier = '';
        if (Campaign && (Campaign.CampaignSubType === 'ShoppingSmartAds' || Campaign.CampaignType === 'PerformanceMax')) {
          multiplier = this.i18n.getString('TargetingSettings_Grid_Column_BidAdj_Automatic');
        } else if (isXandrServing(_.result(Campaign, 'CampaignSubType'), _.result(Campaign, 'CampaignSettings')) || this.isHideBidAdjustmentForBoostEnabled || (Campaign && Campaign.CampaignType === 'LinkedIn')) {
          if ((Criterion.Type === 'Device' && CriterionBid.Multiplier === -100) || !Id || Id <= 0) {
            multiplier = this.i18n.getString('TargetingSettings_Grid_Column_BidAdj_Disabled');
          } else {
            multiplier = this.i18n.getString('TargetingSettings_Grid_Column_BidAdj_Enabled');
          }
        } else {
          const formattedValue = this.formatter(Math.abs(CriterionBid.Multiplier) / 100);
          if (CriterionBid.Multiplier < 0) {
            multiplier = `- ${formattedValue}`;
          } else {
            multiplier = `+ ${formattedValue}`;
          }
        }

        const isCommission = _.chain(Campaign).result('BiddingScheme').result('Type').value() === 'Commission';
        const isAdGroupLevel = this.level === TargetingLevel.AdGroup;
        const criterionType = _.chain(Criterion).result('Type').value();
        const isLocation = criterionType === 'Location' || criterionType === 'Radius';
        const isAdSchedule = criterionType === 'DayTime';
        const isDevice = criterionType === 'Device';

        if (isCommission) {
          if (isLocation || isAdSchedule || (isAdGroupLevel && isDevice)) {
            multiplier = this.i18n.getString('TargetingSettings_Grid_Column_BidAdj_NotApplicable');
          }
        }

        return { multiplier, inherited };
      },
      key: this.config.property,
    };
  }

  template({ value }) {
    if (_.isNull(value)) {
      return bidAdjColumnTemplate({});
    }

    return bidAdjColumnTemplate({
      multiplier: value.multiplier,
      inherited: value.inherited,
    });
  }
}
